import { useEffect, useId, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Dialog as MUIDialog,
} from '@mui/material'
import PropTypes from 'prop-types'
import { getFocusable } from '~/utils'
import SubmitButton from './SubmitButton'

const Dialog = (props) => {
  const { actions, body, error, persistant, title, onClose, ...rest } = props

  const id = useId()
  const bodyRef = useRef()
  const actionRefs = useRef({})

  useEffect(() => {
    if (!rest.open) return
    const intId = setTimeout(() => {
      const focusable = getFocusable(bodyRef.current)
      if (focusable.length > 0) {
        return focusable[0].focus()
      }
      const actionButtonIndex = actions?.findIndex((id) => {
        return !['cancel', 'close'].includes(id)
      })
      if (actionButtonIndex >= 0) {
        actionRefs.current[actionButtonIndex]?.focus?.()
      }
    }, 0)
    return () => clearTimeout(intId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.open])

  return (
    <MUIDialog
      {...rest}
      onClick={(e) => {
        e.stopPropagation()
        rest.onClick?.(e)
      }}
      onClose={(_, reason) => {
        if (
          !persistant ||
          !['backdropClick', 'escapeKeyDown'].includes(reason)
        ) {
          onClose()
        }
      }}
      aria-labelledby={`modal-modal-title-${id}`}
      aria-describedby={`modal-modal-description-${id}`}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        {title && (
          <DialogTitle
            id={`modal-modal-title-${id}`}
            sx={{
              color: (theme) => (error ? theme.palette.error.main : undefined),
            }}
          >
            {title}
          </DialogTitle>
        )}
        {!persistant && (
          <IconButton
            aria-label="close"
            onClick={(e) => {
              e.stopPropagation()
              onClose?.()
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        {body && (
          <DialogContent
            ref={bodyRef}
            dividers
            id={`modal-modal-description-${id}`}
          >
            {typeof body === 'function' ? (
              body()
            ) : (
              <DialogContentText as="div">{body}</DialogContentText>
            )}
          </DialogContent>
        )}
        {actions?.length > 0 && (
          <DialogActions>
            {actions.map(({ id, name, ...rest }, index) => (
              <SubmitButton
                key={index}
                color={error && id === 'confirm' ? 'error' : undefined}
                {...rest}
                ref={(ref) => (actionRefs.current[index] = ref)}
                type={id === 'confirm' ? 'submit' : 'button'}
                onClick={(e) => {
                  e.stopPropagation()
                  onClose(id ?? index)
                }}
              >
                {name}
              </SubmitButton>
            ))}
          </DialogActions>
        )}
      </form>
    </MUIDialog>
  )
}

Dialog.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string.isRequired,
    })
  ),
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  error: PropTypes.bool,
  persistant: PropTypes.bool,
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default Dialog
